// frontend/src/services/api.js

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ============================================================
// AXIOS INTERCEPTORS FOR DEBUGGING
// ============================================================

// Request Interceptor - Log all outgoing requests
axios.interceptors.request.use(
  config => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase() || 'GET'} ${config.url}`);
    console.log('📦 Headers:', config.headers);
    if (config.data) {
      // Don't log file uploads (FormData)
      if (config.data instanceof FormData) {
        console.log('📝 Data: [FormData]');
      } else {
        console.log('📝 Data:', config.data);
      }
    }
    if (config.params) {
      console.log('🔍 Params:', config.params);
    }
    return config;
  },
  error => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor - Log all responses
axios.interceptors.response.use(
  response => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase() || 'GET'} ${response.config.url}`);
    console.log('📦 Status:', response.status);
    if (response.data) {
      // Don't log large responses
      if (typeof response.data === 'object' && response.data.length > 100) {
        console.log(`📝 Data: [${response.data.length} items]`);
      } else {
        console.log('📝 Data:', response.data);
      }
    }
    return response;
  },
  error => {
    console.error('❌ Response Error:');
    console.error('📦 Status:', error.response?.status);
    console.error('📝 Data:', error.response?.data);
    console.error('📝 Message:', error.message);
    console.error('📝 URL:', error.config?.url);
    return Promise.reject(error);
  }
);

// ============================================================
// API METHODS
// ============================================================

const api = {
  // ============================================================
  // AUTH
  // ============================================================
  login: async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    return response.data;
  },

  logout: async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  getProfile: async () => {
    const response = await axios.get(`${API_URL}/auth/profile`);
    return response.data;
  },

  // ============================================================
  // LEADS
  // ============================================================
  getSalesReps: async () => {
    const response = await axios.get(`${API_URL}/leads/sales-reps`);
    return response.data;
  },

  getLeads: async (filters = {}) => {
    // Add cache buster to prevent 304 responses
    const params = {
      ...filters,
      _t: Date.now()
    };
    const response = await axios.get(`${API_URL}/leads`, { params });
    return response.data;
  },

  getLead: async (id) => {
    const response = await axios.get(`${API_URL}/leads/${id}`);
    return response.data;
  },

  createLead: async (data) => {
    const response = await axios.post(`${API_URL}/leads`, data);
    return response.data;
  },

  updateLead: async (id, data) => {
    const response = await axios.put(`${API_URL}/leads/${id}`, data);
    return response.data;
  },

  deleteLead: async (id) => {
    const response = await axios.delete(`${API_URL}/leads/${id}`);
    return response.data;
  },

  getStats: async () => {
    // Add cache buster to prevent 304 responses
    const response = await axios.get(`${API_URL}/stats`, {
      params: { _t: Date.now() }
    });
    return response.data;
  },

  // ============================================================
  // MICROSOFT BOOKINGS
  // ============================================================
  getMicrosoftBookingServices: async () => {
    const response = await axios.get(`${API_URL}/microsoft-bookings/services`);
    return response.data;
  },

  getMicrosoftBookingAvailability: async (date, serviceId) => {
    const response = await axios.get(`${API_URL}/microsoft-bookings/availability`, {
      params: { date, serviceId }
    });
    return response.data;
  },

  bookMicrosoftDemo: async (data) => {
    const response = await axios.post(`${API_URL}/microsoft-bookings/book`, data);
    return response.data;
  },

  getMicrosoftAppointment: async (appointmentId) => {
    const response = await axios.get(`${API_URL}/microsoft-bookings/appointment/${appointmentId}`);
    return response.data;
  },

  cancelMicrosoftDemo: async (leadId, message = '') => {
    const response = await axios.post(`${API_URL}/microsoft-bookings/cancel/${leadId}`, { message });
    return response.data;
  },

  bookDemo: async (leadId, date, time, notes = '') => {
    const response = await axios.post(`${API_URL}/demo`, { leadId, date, time, notes });
    return response.data;
  },

  // ============================================================
  // ACTIVITIES
  // ============================================================
  addActivity: async (data) => {
    const response = await axios.post(`${API_URL}/activities`, data);
    return response.data;
  },

  getActivities: async (leadId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/activities`);
    return response.data;
  },

  // ============================================================
  // MASTER DATA
  // ============================================================
  getMasterData: async (type) => {
    const response = await axios.get(`${API_URL}/master/${type}`);
    return response.data;
  },

  createMasterData: async (type, data) => {
    const response = await axios.post(`${API_URL}/master/${type}`, data);
    return response.data;
  },

  updateMasterData: async (type, id, data) => {
    const response = await axios.put(`${API_URL}/master/${type}/${id}`, data);
    return response.data;
  },

  deleteMasterData: async (type, id) => {
    const response = await axios.delete(`${API_URL}/master/${type}/${id}`);
    return response.data;
  },

  // ============================================================
  // ADMIN - USERS
  // ============================================================
  getUsers: async () => {
    const response = await axios.get(`${API_URL}/admin/users`);
    return response.data;
  },

  createUser: async (data) => {
    const response = await axios.post(`${API_URL}/admin/users`, data);
    return response.data;
  },

  updateUser: async (id, data) => {
    const response = await axios.put(`${API_URL}/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await axios.delete(`${API_URL}/admin/users/${id}`);
    return response.data;
  },

  // ============================================================
  // DOCUMENTS
  // ============================================================
  uploadDocuments: async (leadId, formData, onProgress) => {
    const response = await axios.post(
      `${API_URL}/leads/${leadId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
      }
    );
    return response.data;
  },

  getDocuments: async (leadId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/documents`);
    return response.data;
  },

  deleteDocument: async (leadId, documentId) => {
    const response = await axios.delete(`${API_URL}/leads/${leadId}/documents/${documentId}`);
    return response.data;
  },

  getDocumentUrl: async (leadId, documentId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/documents/${documentId}/url`);
    return response.data;
  },

  downloadDocument: async (leadId, documentId) => {
    const response = await axios.get(`${API_URL}/leads/${leadId}/documents/${documentId}/download`, {
      responseType: 'blob'
    });
    return {
      blob: response.data,
      contentDisposition: response.headers['content-disposition'] || ''
    };
  },

  replaceDocument: async (leadId, documentId, formData, onProgress) => {
    const response = await axios.put(
      `${API_URL}/leads/${leadId}/documents/${documentId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
      }
    );
    return response.data;
  },

  // ============================================================
  // BULK IMPORT
  // ============================================================
  getImportOptions: async () => {
    const response = await axios.get(`${API_URL}/leads/import/options`);
    return response.data;
  },

  validateBulkImportLeads: async (payload) => {
    const response = await axios.post(`${API_URL}/leads/bulk-import/validate`, payload);
    return response.data;
  },
  validateImportAgents: async (agentNames) => {
    const response = await axios.post(`${API_URL}/leads/import/validate-agents`, { agentNames });
    return response.data;
  },

  createImportUser: async (data) => {
    const response = await axios.post(`${API_URL}/leads/import/create-user`, data);
    return response.data;
  },

  bulkImportLeads: async (payload) => {
    const body = Array.isArray(payload) ? { leads: payload } : payload;
    const response = await axios.post(`${API_URL}/leads/bulk-import`, body);
    return response.data;
  },

  getImportStatus: async (importId) => {
    const response = await axios.get(`${API_URL}/leads/import/${importId}/status`);
    return response.data;
  },

  getImportTemplate: async () => {
    const response = await axios.get(`${API_URL}/leads/import/template`);
    return response.data;
  },

/* ============================================================
   OPTIMIZED DASHBOARD APIs
   These are separate from the existing getStats/getLeads APIs.
   ============================================================ */

  getDashboardStateCounts: async () => {
    const response = await axios.get(`${API_URL}/dashboard/state-counts` );
    return response.data;
  },

  getDashboardRecentActivities: async () => {
    const response = await axios.get(
      `${API_URL}/dashboard/recent-activities`
    );

    return response.data;
  },

  getDashboardUpcomingDemos: async () => {
    const response = await axios.get(
      `${API_URL}/dashboard/upcoming-demos`
    );

    return response.data;
  },
getPaginatedLeads: async (
  filters = {},
  page = 1,
  limit = 30
) => {
  const params = {
    page,
    limit
  };

  if (
    filters.status &&
    filters.status !== 'all'
  ) {
    params.status =
      filters.status;
  }

  if (filters.search) {
    params.search =
      filters.search;
  }

  if (filters.category) {
    params.category =
      filters.category;
  }

  if (filters.subcategory) {
    params.subcategory =
      filters.subcategory;
  }

  if (
    Array.isArray(
      filters.products
    ) &&
    filters.products.length > 0
  ) {
    params.products =
      filters.products;
  }

  if (
    Array.isArray(
      filters.dmsProviders
    ) &&
    filters.dmsProviders.length > 0
  ) {
    params.dmsProviders =
      filters.dmsProviders;
  }

  if (
    filters.leadType &&
    filters.leadType !== 'all'
  ) {
    params.leadType =
      filters.leadType;
  }

  if (
    filters.leadSource &&
    filters.leadSource !== 'all'
  ) {
    params.leadSource =
      filters.leadSource;
  }

  if (
    filters.serviceDashboard &&
    filters.serviceDashboard !== 'all'
  ) {
    params.serviceDashboard =
      filters.serviceDashboard;
  }

  if (
    filters.salesRep &&
    filters.salesRep !== 'all'
  ) {
    params.salesRep =
      filters.salesRep;
  }

  const response = await axios.get(
    `${API_URL}/leads/paginated`,
    {
      params
    }
  );

  return response.data;
},

getLeadStateCounts: async (
  filters = {}
) => {
  const params = {};

  if (
    filters.status &&
    filters.status !== 'all'
  ) {
    params.status =
      filters.status;
  }

  if (filters.search) {
    params.search =
      filters.search;
  }

  if (filters.category) {
    params.category =
      filters.category;
  }

  if (filters.subcategory) {
    params.subcategory =
      filters.subcategory;
  }

  if (
    Array.isArray(
      filters.products
    ) &&
    filters.products.length > 0
  ) {
    params.products =
      filters.products;
  }

  if (
    Array.isArray(
      filters.dmsProviders
    ) &&
    filters.dmsProviders.length > 0
  ) {
    params.dmsProviders =
      filters.dmsProviders;
  }

  if (
    filters.leadType &&
    filters.leadType !== 'all'
  ) {
    params.leadType =
      filters.leadType;
  }

  if (
    filters.leadSource &&
    filters.leadSource !== 'all'
  ) {
    params.leadSource =
      filters.leadSource;
  }

  if (
    filters.serviceDashboard &&
    filters.serviceDashboard !== 'all'
  ) {
    params.serviceDashboard =
      filters.serviceDashboard;
  }

  if (
    filters.salesRep &&
    filters.salesRep !== 'all'
  ) {
    params.salesRep =
      filters.salesRep;
  }

  const response = await axios.get(
    `${API_URL}/leads/state-counts`,
    {
      params
    }
  );

  return response.data;
},

exportLeads: async (filters = {}) => {
  const params = {};

  if (filters.status && filters.status !== 'all') {
    params.status = filters.status;
  }

  if (filters.search && filters.search.trim()) {
    params.search = filters.search.trim();
  }

  if (filters.leadType && filters.leadType !== 'all') {
    params.leadType = filters.leadType;
  }

  if (filters.leadSource && filters.leadSource !== 'all') {
    params.leadSource = filters.leadSource;
  }

  if (
    filters.serviceDashboard &&
    filters.serviceDashboard !== 'all'
  ) {
    params.serviceDashboard = filters.serviceDashboard;
  }

  if (filters.salesRep && filters.salesRep !== 'all') {
    params.salesRep = filters.salesRep;
  }

  if (
    Array.isArray(filters.dmsProviders) &&
    filters.dmsProviders.length
  ) {
    params.dmsProviders = filters.dmsProviders;
  }

  if (
    Array.isArray(filters.products) &&
    filters.products.length
  ) {
    params.products = filters.products;
  }

  const response = await axios.get(
    `${API_URL}/leads/export`,
    { params }
  );

  return response.data;
}
  };
export default api;